import type * as React from 'react'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/shared/lib/cn'

const badgeVariants = cva(
  'inline-flex items-center justify-center border [font-size:var(--type-eyebrow-font-size)] [font-weight:var(--type-eyebrow-font-weight)] [line-height:var(--type-eyebrow-line-height)] w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-2 aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary: 'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive-soft text-destructive-soft-foreground [a&]:hover:bg-destructive-soft/90 focus-visible:ring-destructive/20',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        info: 'border-transparent bg-info-soft text-info-soft-foreground [a&]:hover:bg-info-soft/90',
        success: 'border-transparent bg-success-soft text-success-soft-foreground [a&]:hover:bg-success-soft/90',
        warning: 'border-transparent bg-warning-soft text-warning-soft-foreground [a&]:hover:bg-warning-soft/90',
      },
      size: {
        default: 'gap-1 rounded-[5px] px-2 py-0.5',
        // Плашка-таблетка: крупнее и заметнее обычной. Для мест, где плашка — самостоятельный
        // элемент страницы (надзаголовок первого экрана, перечисление площадок), а не пометка
        // в строке списка.
        lg: 'gap-2 rounded-full px-3 py-1.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function Badge({
  className,
  variant,
  size,
  render = <span />,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { render?: useRender.RenderProp }) {
  return useRender({
    render,
    props: {
      'data-slot': 'badge',
      className: cn(badgeVariants({ variant, size }), className),
      ...props,
    },
  })
}

export { Badge, badgeVariants }
